import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { WebContainerService } from '../services/webcontainer';

interface PreviewPaneProps {
  code: string;
  isVisible: boolean;
  onToggleVisibility: () => void;
}

export default function PreviewPane({ code, isVisible, onToggleVisibility }: PreviewPaneProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const webcontainerService = WebContainerService.getInstance();

  useEffect(() => {
    initializeWebContainer();
  }, []);

  useEffect(() => {
    if (code.trim() && webcontainerService.isReady()) {
      updatePreview(code);
    }
  }, [code]);

  const initializeWebContainer = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Subscribe to WebContainer output
      const unsubscribe = webcontainerService.onOutput((output) => {
        setLogs(prev => [...prev, output]);
        
        // Check if the output contains a server-ready message and extract URL
        if (output.includes('Server ready at')) {
          const urlMatch = output.match(/Server ready at (https?:\/\/[^\s\n]+)/);
          if (urlMatch) {
            setPreviewUrl(urlMatch[1]);
          }
        }
      });
      
      await webcontainerService.boot();
      
      // Get the preview URL
      const url = webcontainerService.getPreviewUrl();
      if (url === 'fallback-mode') {
        setError('WebContainer requires cross-origin isolation headers. Please add Cross-Origin-Embedder-Policy: require-corp and Cross-Origin-Opener-Policy: same-origin to your server.');
        setIsLoading(false);
        return unsubscribe;
      } else if (url) {
        setPreviewUrl(url);
      }
      
      setIsLoading(false);
      
      // Cleanup subscription on unmount
      return unsubscribe;
    } catch (err) {
      console.error('Failed to initialize WebContainer:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize preview environment');
      setIsLoading(false);
    }
  };

  const updatePreview = async (sourceCode: string) => {
    if (!webcontainerService.isReady()) return;

    try {
      setIsUpdating(true);
      setError(null);

      // Transform and update component
      const componentCode = transformReactCode(sourceCode);
      await webcontainerService.updateComponent(componentCode);
      
      // Refresh the iframe after a short delay to allow for compilation
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = previewUrl || '';
        }
        setIsUpdating(false);
      }, 1000);
      
    } catch (err) {
      console.error('Failed to update preview:', err);
      setError(err instanceof Error ? err.message : 'Failed to update preview');
      setIsUpdating(false);
    }
  };

  const transformReactCode = (code: string): string => {
    // If the code already has imports and exports, use it as-is
    if (code.includes('import') && code.includes('export')) {
      return code;
    }

    // If it's just a function component, wrap it properly
    if (code.includes('function Component') || code.includes('const Component')) {
      return `
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

${code}

export default Component;
      `.trim();
    }

    // If it's a function with a different name, try to detect and export it
    const functionMatch = code.match(/function\s+(\w+)/);
    if (functionMatch) {
      const functionName = functionMatch[1];
      return `
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

${code}

export default ${functionName};
      `.trim();
    }

    // Default case: wrap the code as a Component function
    return `
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

function Component() {
  ${code.includes('return') ? code : `return (${code});`}
}

export default Component;
    `.trim();
  };

  const refreshPreview = () => {
    if (iframeRef.current && previewUrl) {
      iframeRef.current.src = previewUrl;
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <h2 className="text-sm font-medium text-gray-700">Live Preview</h2>
          {isUpdating && (
            <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
          )}
          <div className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
            React
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowLogs(!showLogs)}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${
              showLogs 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {showLogs ? 'Preview' : 'Logs'}
          </button>
          <button
            onClick={refreshPreview}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
            title="Refresh preview"
            disabled={!previewUrl}
          >
            <RefreshCw size={16} />
          </button>
          <button
            onClick={onToggleVisibility}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
            title={isVisible ? 'Hide preview' : 'Show preview'}
          >
            {isVisible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      {!isVisible ? (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <div className="text-center">
            <EyeOff className="w-12 h-12 mx-auto mb-4" />
            <p className="text-sm">Preview hidden</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400">
                <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-sm">Initializing WebContainer...</p>
                <p className="text-xs text-gray-500 mt-2">This may take a moment on first load</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-red-500 max-w-md mx-auto p-4">
                <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                <p className="text-sm font-medium mb-2">Preview Error</p>
                <p className="text-xs text-gray-600 bg-gray-100 p-3 rounded-md text-left font-mono">
                  {error}
                </p>
                {error.includes('cross-origin isolation headers') && (
                  <div className="mt-4 text-xs text-gray-600 bg-blue-50 p-3 rounded-md text-left">
                    <p className="font-medium mb-2">Required Server Headers:</p>
                    <code className="block bg-gray-100 p-2 rounded text-xs">
                      Cross-Origin-Embedder-Policy: require-corp<br/>
                      Cross-Origin-Opener-Policy: same-origin
                    </code>
                  </div>
                )}
                <button
                  onClick={initializeWebContainer}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : previewUrl ? (
            <div className="h-full flex flex-col">
              {showLogs ? (
                <div className="flex-1 bg-gray-900 text-green-400 font-mono text-sm p-4 overflow-auto">
                  <div className="mb-4">
                    <div className="text-xs text-gray-500 mb-4">
                      React development server logs:
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    {logs.length > 0 ? (
                      logs.map((log, index) => (
                        <div key={index} className="text-green-400 whitespace-pre-wrap">
                          {log}
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500">
                        React server logs will appear here...
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  src={previewUrl}
                  className="w-full h-full border-0"
                  title="Live Preview"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                />
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400">
                <Eye className="w-12 h-12 mx-auto mb-4" />
                <p className="text-sm">Preparing preview environment...</p>
                <p className="text-xs text-gray-500 mt-2">Waiting for server to start</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}